import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lightbulb, X, Loader2 } from "lucide-react";

const STATE = {
  pending:  { label: "Pending",  cls: "border-[var(--gold)] text-[var(--warn)] bg-[var(--gold-soft)]" },
  accepted: { label: "Accepted", cls: "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]" },
  declined: { label: "Declined", cls: "border-[var(--line)] text-[var(--ink-2)] bg-[var(--bg-2)]" },
};

// Sidebar "Suggest" button + modal: staff send module/request ideas to the admin's Build Log.
export function SuggestButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="suggest-btn" onClick={() => setOpen(true)}
        className="w-full flex items-center gap-1.5 text-xs px-2 py-1.5 mb-2 rounded-sm border border-[var(--gold)] text-[var(--navy)] bg-[var(--gold-soft)] hover:brightness-95 transition">
        <Lightbulb size={13} strokeWidth={1.75} /> Suggest an idea
      </button>
      {open && <SuggestModal onClose={() => setOpen(false)} />}
    </>
  );
}

function SuggestModal({ onClose }) {
  const [modules, setModules] = useState([]);
  const [module, setModule] = useState("");
  const [request, setRequest] = useState("");
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadMine = () => api.get("/suggestions/mine").then((r) => setMine(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/suggestions/modules").then((r) => setModules(r.data)).catch(() => {});
    loadMine();
  }, []);

  async function submit() {
    if (!module) { toast.error("Pick a module"); return; }
    if (request.trim().length < 3) { toast.error("Describe your suggestion"); return; }
    setBusy(true);
    try {
      await api.post("/suggestions", { module, request: request.trim() });
      toast.success("Thanks — your suggestion has been sent to the admin.");
      setRequest("");
      loadMine();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send suggestion");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="suggest-modal">
      <div className="bg-white rounded-sm w-full max-w-lg">
        <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--line)]">
          <div className="font-medium text-sm flex items-center gap-1.5"><Lightbulb size={14} className="text-[var(--gold)]" /> Suggest an improvement</div>
          <button data-testid="suggest-close" onClick={onClose} className="text-[var(--ink-2)] hover:text-[var(--ink)]"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-2.5">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] block mb-0.5">Module</label>
            <select data-testid="suggest-module" value={module} onChange={(e) => setModule(e.target.value)} className="input-slim w-full text-xs">
              <option value="">— Select module —</option>
              {modules.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] block mb-0.5">Request</label>
            <textarea data-testid="suggest-request" rows={4} maxLength={2000} value={request} onChange={(e) => setRequest(e.target.value)}
              placeholder="What would make your work easier?" className="input-slim w-full text-xs" />
          </div>
          <div className="flex justify-end">
            <button data-testid="suggest-submit" onClick={submit} disabled={busy} className="btn-primary flex items-center gap-1.5 text-xs !py-1.5 !px-3">
              {busy && <Loader2 size={12} className="animate-spin" />} Send suggestion
            </button>
          </div>
        </div>
        <div className="px-4 pb-4">
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] mb-1 pt-3 border-t border-[var(--line)]">My suggestions</div>
          {mine.length === 0 ? (
            <div className="text-xs text-[var(--ink-2)]" data-testid="my-suggestions-empty">You haven't sent any suggestions yet.</div>
          ) : (
            <div className="max-h-48 overflow-y-auto divide-y divide-[var(--line)]" data-testid="my-suggestions">
              {mine.map((m) => (
                <div key={m.id} className="py-1.5 flex items-start gap-2 text-xs" data-testid={`my-suggestion-${m.id}`}>
                  <span className="font-mono-data text-[10px] text-[var(--ink-2)] whitespace-nowrap pt-px">{m.date}</span>
                  <span className="text-[10px] text-[var(--ink-2)] whitespace-nowrap pt-px">{m.module}</span>
                  <span className="flex-1 min-w-0 truncate" title={m.request}>{m.request}</span>
                  <span className={`text-[9px] uppercase tracking-wider px-1 py-px rounded-sm border whitespace-nowrap ${STATE[m.state].cls}`}>{STATE[m.state].label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
